/**
 * VaultTreePane — folder/file tree for the in-memory vault model.
 *
 * Pure-presentational. Takes a flat list of vault notes (parser output)
 * and groups them by folder for collapsible rendering. Click → onOpen
 * call which routes through the parent's pane-A/B decision.
 */

import { useMemo, useState } from 'react';
import { testIds } from '../../lib/testIds';
import type { ParsedNote } from '../../lib/vault/parse';

export interface VaultTreePaneProps {
  notes: ParsedNote[];
  activePath: string | null;
  onOpen: (path: string, modifier: 'pane-a' | 'pane-b') => void;
}

interface FolderNode {
  name: string;
  fullPath: string;
  folders: Map<string, FolderNode>;
  notes: ParsedNote[];
}

function buildTree(notes: ParsedNote[]): FolderNode {
  const root: FolderNode = {
    name: '',
    fullPath: '',
    folders: new Map(),
    notes: [],
  };
  for (const n of notes) {
    const segments = n.path.split('/');
    const last = segments.pop()!;
    let cursor = root;
    let acc = '';
    for (const seg of segments) {
      acc = acc ? `${acc}/${seg}` : seg;
      let child = cursor.folders.get(seg);
      if (!child) {
        child = { name: seg, fullPath: acc, folders: new Map(), notes: [] };
        cursor.folders.set(seg, child);
      }
      cursor = child;
    }
    // attach the note at its parent folder
    cursor.notes.push(n);
    // record basename for sort stability (not used directly here)
    void last;
  }
  return root;
}

function Folder({
  node,
  depth,
  activePath,
  expanded,
  onToggle,
  onOpen,
}: {
  node: FolderNode;
  depth: number;
  activePath: string | null;
  expanded: Set<string>;
  onToggle: (path: string) => void;
  onOpen: (path: string, modifier: 'pane-a' | 'pane-b') => void;
}) {
  const isOpen = depth === 0 || expanded.has(node.fullPath);
  const childFolders = [...node.folders.values()].sort((a, b) =>
    a.name.localeCompare(b.name),
  );
  const childNotes = [...node.notes].sort((a, b) =>
    a.name.localeCompare(b.name),
  );
  return (
    <>
      {depth > 0 && (
        <div
          className={`vault-tree-node vault-tree-folder`}
          data-testid={testIds.obsidianVault.treeNode(node.fullPath)}
          style={{ paddingLeft: depth * 12 + 4 }}
          onClick={() => onToggle(node.fullPath)}
        >
          {isOpen ? '▾' : '▸'} {node.name}
        </div>
      )}
      {isOpen && (
        <>
          {childFolders.map((c) => (
            <Folder
              key={c.fullPath}
              node={c}
              depth={depth + 1}
              activePath={activePath}
              expanded={expanded}
              onToggle={onToggle}
              onOpen={onOpen}
            />
          ))}
          {childNotes.map((n) => {
            // B.5 — show a "Draft" badge for NIPE-authored notes.
            // Marker: frontmatter.source === 'nipe' (set by
            // markNipeAuthored in src/lib/nipe/markNipeAuthored.ts).
            // Filename prefix nipe_ai_ is a secondary signal; either
            // is sufficient to render the badge.
            const fm = (n.frontmatter ?? {}) as Record<string, unknown>;
            const isNipeAuthored =
              fm.source === 'nipe' || n.name.startsWith('nipe_ai_');
            return (
              <div
                key={n.path}
                data-testid={testIds.obsidianVault.treeNode(n.path)}
                className={`vault-tree-node${
                  n.path === activePath ? ' vault-tree-node-active' : ''
                }`}
                style={{ paddingLeft: (depth + 1) * 12 + 4 }}
                onClick={(e) =>
                  onOpen(n.path, e.metaKey || e.ctrlKey || e.shiftKey ? 'pane-b' : 'pane-a')
                }
                title={n.path}
              >
                <span>{n.name}</span>
                {isNipeAuthored ? (
                  <span
                    data-testid={testIds.obsidianVault.nipeDraftBadge(n.path)}
                    className="ml-1 inline-block px-1 text-[10px] leading-tight align-middle bg-yellow-100 text-black border border-black"
                    title="Authored by NIPE — review before sharing"
                  >
                    Draft
                  </span>
                ) : null}
              </div>
            );
          })}
        </>
      )}
    </>
  );
}

export function VaultTreePane({
  notes,
  activePath,
  onOpen,
}: VaultTreePaneProps) {
  const root = useMemo(() => buildTree(notes), [notes]);
  // Default all folders expanded so the fixture vault is immediately
  // readable; user collapses by clicking the chevron.
  const initialExpanded = useMemo(() => {
    const set = new Set<string>();
    function walk(node: FolderNode) {
      if (node.fullPath) set.add(node.fullPath);
      for (const c of node.folders.values()) walk(c);
    }
    walk(root);
    return set;
  }, [root]);
  const [expanded, setExpanded] = useState<Set<string>>(initialExpanded);

  const handleToggle = (path: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  };

  return (
    <div
      data-testid={testIds.obsidianVault.treePane}
      style={{
        height: '100%',
        overflowY: 'auto',
        background: '#f0f0f0',
        borderRight: '1px solid #808080',
      }}
    >
      {notes.length === 0 ? (
        <div className="vault-editor-empty">No notes loaded.</div>
      ) : (
        <Folder
          node={root}
          depth={0}
          activePath={activePath}
          expanded={expanded}
          onToggle={handleToggle}
          onOpen={onOpen}
        />
      )}
    </div>
  );
}
